import { runInAction, set } from 'mobx';

import type { IImportColumn, IPreviewRow } from '@feathr/blackbox';
import { defaultPersonAttributes } from '@feathr/blackbox';

export const SAMPLE_DISPLAY_LENGTH = 60;
export const SAMPLE_TITLE_LENGTH = 200;

/*
 * Maps each saved column name to the first non-empty value in that column of
 * the preview's input rows. A column with no value maps to an empty string.
 * Input rows are keyed by the import's saved column names, so a headerless
 * file is keyed `Column 1` to `Column N`.
 */
export function getSampleValues(
  rows: IPreviewRow[],
  columnNames: string[],
): Record<string, string> {
  return Object.fromEntries(
    columnNames.map((columnName) => {
      const key = String(columnName);
      const sample = rows
        .map((row) => toCellText(row.input?.[key] as unknown))
        .find((text) => text.trim() !== '');
      return [key, sample ?? ''];
    }),
  );
}

/**
 * The distinct columns of one previewed row whose value the mapped field's type could not read.
 * A short row's dropped cells (`unreachable_column`) are the row's own problem, not the
 * mapping's, so they are left out.
 */
export function unreadableColumns(row: IPreviewRow): string[] {
  return [
    ...new Set(
      (row.problems ?? [])
        .filter((problem) => problem.reason === 'unparseable_cell')
        .map((problem) => problem.column_name),
    ),
  ];
}

/** Each column with an unreadable cell in the preview, against the first such value. */
export function getUnreadableValues(rows: IPreviewRow[]): Map<string, string> {
  const values = new Map<string, string>();
  for (const row of rows) {
    for (const columnName of unreadableColumns(row)) {
      // A column named `constructor` or `toString` would otherwise read off Object.prototype.
      const cell = row.input && Object.hasOwn(row.input, columnName) ? row.input[columnName] : '';
      const value = toCellText(cell as unknown);
      if (!values.has(columnName) && value.trim() !== '') {
        values.set(columnName, value);
      }
    }
  }
  return values;
}

// Input rows are typed as strings, but the preview can carry numbers, booleans or nested values.
function toCellText(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value.toString();
  }
  return JSON.stringify(value) ?? '';
}

export function truncateSample(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
}

/*
 * Maps a column to the built-in field of the same id (e.g. a column named "email"), unless the
 * column already maps to something.
 */
export function matchBuiltInField(column: IImportColumn): void {
  if (column.feathr_attr) {
    return;
  }
  const columnName = String(column.column_name).trim().toLowerCase();
  const attr = defaultPersonAttributes.find(({ id }) => id === columnName);
  if (attr) {
    runInAction(() => {
      set(column, { feathr_attr: attr.id, attr_type: attr.data_type });
    });
  }
}

/** The first column of the file that no mapping row uses yet. */
export function getNextUnmappedColumn(
  columnNames: string[],
  columnMappers: IImportColumn[],
): string | undefined {
  const used = new Set(columnMappers.map((column) => String(column.column_name)));
  return columnNames.find((columnName) => !used.has(String(columnName)));
}
